import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

interface Experience {
  company: string;
  role: string;
  duration: string;
  description: string;
}

const experiences: Experience[] = [
  {
    company: "Rextsoft Inc",
    role: "Freelance Developer",
    duration: "2023 — 2023",
    description: "Fullstack development with React/Node.js.",
  },
  {
    company: "Evenesis",
    role: "Software Developer Intern",
    duration: "2024 — 2025",
    description: "Built Liveness Detection & Crowd Management systems.",
  },
  {
    company: "Evenesis",
    role: "Software Developer",
    duration: "2025 — Present",
    description: "Maintaining event management microservices (Angular/.NET).",
  },
];

const WorkExperience: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Smooth transition: Dark -> White -> Dark
  const backgroundColor = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], ["#09090b", "#f4f4f5", "#f4f4f5", "#09090b"]);
  const textColor = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], ["#ffffff", "#18181b", "#18181b", "#ffffff"]);
  const borderColor = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], ["rgba(255,255,255,0.1)", "rgba(24,24,27,1)", "rgba(24,24,27,1)", "rgba(255,255,255,0.1)"]);
  const subTextColor = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], ["#a1a1aa", "#52525b", "#52525b", "#a1a1aa"]);

  // Column Parallax Animations - Increased range for longer scroll
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const y2 = useTransform(scrollYProgress, [0, 1], [200, -600]); // Moves fastest and starts lower
  const y3 = useTransform(scrollYProgress, [0, 1], [100, -400]);

  // Helper to get the correct Y transform based on index
  const getParallaxY = (index: number) => {
    if (index % 3 === 0) return y1;
    if (index % 3 === 1) return y2;
    return y3;
  };

  return (
    <motion.section
      id="experience"
      ref={containerRef}
      style={{ backgroundColor, color: textColor }}
      className="pt-[40vh] md:pt-[80vh] pb-16 md:pb-32 px-4 md:px-12 lg:px-24 relative min-h-[250vh] md:min-h-[400vh] flex flex-col justify-start"
    >
      {/* Massive Header - Matching Education Style */}
      <motion.div
        style={{ borderColor: borderColor }}
        className="mb-16 md:mb-32 border-b pb-8 md:pb-12"
      >
        <h2 className="text-[12vw] md:text-[8vw] leading-[0.8] font-serif font-black tracking-tighter">
          WORK<br />EXPERIENCE
        </h2>
      </motion.div>

      {/* Grid with Staggered Parallax and Wide Gaps */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-16 lg:gap-32 w-full max-w-[95vw] md:max-w-[90vw] mx-auto">
        {experiences.map((exp, i) => (
          <motion.div
            key={i}
            style={{
              y: getParallaxY(i),
            }}
            className={`group w-full aspect-[3/4] rounded-3xl border border-white/10 bg-gradient-to-br from-black/80 via-black/70 to-black/80 backdrop-blur-md shadow-2xl flex flex-col justify-between p-6 md:p-8 hover:-translate-y-4 hover:shadow-[0_20px_40px_rgba(0,0,0,0.5)] transition-all duration-700 relative overflow-hidden ${i === 1 ? 'mt-8 md:mt-[25vh] lg:mt-[50vh]' : i === 2 ? 'mt-16 md:mt-[50vh] lg:mt-[100vh]' : ''
              }`}
          >
            {/* Glossy Liquid Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-40 group-hover:opacity-60 transition-opacity duration-700 pointer-events-none" />

            {/* Top: Year & Type */}
            <div className="flex justify-between items-start border-b border-white/10 pb-4 relative z-10">
              <span className="font-mono text-xl text-white">{exp.duration.split("—")[0].trim()}</span>
              <span className="font-mono text-xs uppercase tracking-widest text-right text-zinc-400">
                {exp.role.includes("Intern") ? "Internship" : "Full-time"}
              </span>
            </div>

            {/* Middle: Company & Role */}
            <div className="flex flex-col justify-center flex-grow py-6 relative z-10">
              <h3 className="text-4xl lg:text-5xl font-display font-medium mb-4 leading-none text-white">{exp.company}</h3>
              <p className="font-serif text-xl text-zinc-300">{exp.role}</p>
            </div>

            {/* Bottom: Description */}
            <div className="border-t border-white/10 pt-4 relative z-10">
              <motion.p
                style={{ color: subTextColor }}
                className="font-light text-sm leading-relaxed"
              >
                {exp.description}
              </motion.p>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.section >
  );
};

export default WorkExperience;
